import { useState, useMemo } from 'react';
import {
  TextField,
  Autocomplete,
  Checkbox,
  Chip,
  Collapse,
  Button,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FilterListIcon from '@mui/icons-material/FilterList';

import styled from '@/styles';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';

import {
  ONBOARDING_STATUS_OPTIONS,
  DEPENDABILITY_OPTIONS,
  SUSPENSION_STATUS_OPTIONS,
  TAX_TYPE_OPTIONS,
  STATUS_OPTIONS,
  BACKGROUND_STATUS_MAP,
  I9_STATUS_MAP,
} from './StatusMappings';

const I9_DISPLAY_OPTIONS = Object.keys(I9_STATUS_MAP).map((label) => ({
  label,
  value: label,
}));

const BACKGROUND_DISPLAY_OPTIONS = Object.keys(BACKGROUND_STATUS_MAP).map(
  (label) => ({
    label,
    value: label,
  }),
);

export type FilterState = {
  onboarding_status?: string[];
  i9_status?: string[];
  background_status?: string[];
  tax_type?: string;
  active?: string;
  dependability_tier?: string[];
  accountability_status?: string[];
  skill_ids?: number[];
  zip?: string;
  radius?: string;
};

type Props = {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onClear: () => void;
  skills: Array<{ id: number; name: string }>;
  searchValue: string;
  onSearchChange: (value: string) => void;
};

const FilterGrid = styled('div', {
  display: 'grid',
  gap: '16px',
  gridTemplateColumns: '1fr',
  '@tablet': {
    gridTemplateColumns: '1fr 1fr',
  },
  '@smallDesktop': {
    gridTemplateColumns: '1fr 1fr 1fr',
  },
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const getActiveFilterCount = (filters: FilterState): number => {
  let count = 0;
  if (filters.onboarding_status?.length) count++;
  if (filters.i9_status?.length) count++;
  if (filters.background_status?.length) count++;
  if (filters.tax_type) count++;
  if (filters.active) count++;
  if (filters.dependability_tier?.length) count++;
  if (filters.accountability_status?.length) count++;
  if (filters.skill_ids?.length) count++;
  if (filters.zip) count++;
  return count;
};

const getActiveFilterChips = (
  filters: FilterState,
  skills: Array<{ id: number; name: string }>,
): Array<{ key: string; label: string; field: keyof FilterState }> => {
  const chips: Array<{
    key: string;
    label: string;
    field: keyof FilterState;
  }> = [];

  filters.onboarding_status?.forEach((v) => {
    const opt = ONBOARDING_STATUS_OPTIONS.find((o) => o.value === v);
    chips.push({
      key: `onb-${v}`,
      label: `Onboarding: ${opt?.label || v}`,
      field: 'onboarding_status',
    });
  });
  filters.i9_status?.forEach((v) => {
    chips.push({
      key: `i9-${v}`,
      label: `I-9: ${v}`,
      field: 'i9_status',
    });
  });
  filters.background_status?.forEach((v) => {
    chips.push({
      key: `bg-${v}`,
      label: `Background: ${v}`,
      field: 'background_status',
    });
  });
  if (filters.tax_type) {
    chips.push({
      key: 'tax',
      label: `Tax: ${filters.tax_type.toUpperCase()}`,
      field: 'tax_type',
    });
  }
  if (filters.active) {
    chips.push({
      key: 'active',
      label: `Status: ${filters.active === 'true' ? 'Enabled' : 'Disabled'}`,
      field: 'active',
    });
  }
  filters.dependability_tier?.forEach((v) => {
    const opt = DEPENDABILITY_OPTIONS.find((o) => o.value === v);
    chips.push({
      key: `dep-${v}`,
      label: `Dependability: ${opt?.label || v}`,
      field: 'dependability_tier',
    });
  });
  filters.accountability_status?.forEach((v) => {
    const opt = SUSPENSION_STATUS_OPTIONS.find((o) => o.value === v);
    chips.push({
      key: `acc-${v}`,
      label: `Suspension: ${opt?.label || v}`,
      field: 'accountability_status',
    });
  });
  filters.skill_ids?.forEach((id) => {
    const skill = skills.find((s) => s.id === id);
    chips.push({
      key: `skill-${id}`,
      label: `Skill: ${skill?.name || id}`,
      field: 'skill_ids',
    });
  });
  if (filters.zip) {
    chips.push({ key: 'zip', label: `Zip: ${filters.zip}`, field: 'zip' });
  }
  if (filters.radius) {
    chips.push({
      key: 'radius',
      label: `Radius: ${filters.radius} mi`,
      field: 'radius',
    });
  }

  return chips;
};

const WorkerFilterPanel = ({
  filters,
  onFilterChange,
  onClear,
  skills,
  searchValue,
  onSearchChange,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  const activeCount = useMemo(() => getActiveFilterCount(filters), [filters]);
  const chips = useMemo(
    () => getActiveFilterChips(filters, skills),
    [filters, skills],
  );

  const updateFilter = (patch: Partial<FilterState>) => {
    onFilterChange({ ...filters, ...patch });
  };

  const removeChip = (chip: {
    key: string;
    label: string;
    field: keyof FilterState;
  }) => {
    const field = chip.field;
    if (
      field === 'onboarding_status' ||
      field === 'i9_status' ||
      field === 'background_status' ||
      field === 'dependability_tier' ||
      field === 'accountability_status'
    ) {
      const current = (filters[field] as string[]) || [];
      const value = chip.key.split('-').slice(1).join('-');
      updateFilter({ [field]: current.filter((v) => v !== value) });
    } else if (field === 'skill_ids') {
      const current = filters.skill_ids || [];
      const id = parseInt(chip.key.replace('skill-', ''), 10);
      updateFilter({ skill_ids: current.filter((v) => v !== id) });
    } else {
      updateFilter({ [field]: undefined });
    }
  };

  // Find selected skill objects for the autocomplete
  const selectedSkillObjects = useMemo(
    () => skills.filter((s) => filters.skill_ids?.includes(s.id)),
    [skills, filters.skill_ids],
  );

  return (
    <Stack vertical gap={12}>
      <Stack gap={12} style={{ flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          placeholder="Search by name, email, phone..."
          size="small"
          sx={{ minWidth: 250, flex: 1 }}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setExpanded(!expanded)}
          sx={{ textTransform: 'none', minWidth: 120 }}
        >
          Filters
          {activeCount > 0 && (
            <Chip
              label={activeCount}
              size="small"
              color="primary"
              sx={{ ml: 1, height: 20, minWidth: 20 }}
            />
          )}
        </Button>
      </Stack>

      {chips.length > 0 && (
        <Stack gap={8} style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Small color="lighter">Active filters:</Small>
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              onDelete={() => removeChip(chip)}
            />
          ))}
          <Chip
            color="primary"
            label="Clear all"
            size="small"
            variant="outlined"
            onClick={onClear}
          />
        </Stack>
      )}

      <Collapse in={expanded}>
        <FilterGrid>
          {/* Onboarding Status - multi */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={ONBOARDING_STATUS_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={ONBOARDING_STATUS_OPTIONS.filter((o) =>
              filters.onboarding_status?.includes(o.value),
            )}
            onChange={(_, newValue) =>
              updateFilter({
                onboarding_status: newValue.map((v) => v.value),
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Onboarding Status" />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* I-9 Status - multi, simplified */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={I9_DISPLAY_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={I9_DISPLAY_OPTIONS.filter((o) =>
              filters.i9_status?.includes(o.value),
            )}
            onChange={(_, newValue) =>
              updateFilter({ i9_status: newValue.map((v) => v.value) })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="I-9 Status" />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* Background Status - multi, simplified */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={BACKGROUND_DISPLAY_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={BACKGROUND_DISPLAY_OPTIONS.filter((o) =>
              filters.background_status?.includes(o.value),
            )}
            onChange={(_, newValue) =>
              updateFilter({
                background_status: newValue.map((v) => v.value),
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Background Status" />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* Tax Type - single select via Autocomplete */}
          <Autocomplete
            size="small"
            options={TAX_TYPE_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={
              TAX_TYPE_OPTIONS.find((o) => o.value === filters.tax_type) || null
            }
            onChange={(_, newValue) =>
              updateFilter({ tax_type: newValue?.value || undefined })
            }
            renderInput={(params) => <TextField {...params} label="Tax Type" />}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* Status/Active - single select */}
          <Autocomplete
            size="small"
            options={STATUS_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={
              STATUS_OPTIONS.find((o) => o.value === filters.active) || null
            }
            onChange={(_, newValue) =>
              updateFilter({ active: newValue?.value || undefined })
            }
            renderInput={(params) => <TextField {...params} label="Status" />}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* Dependability - multi */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={DEPENDABILITY_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={DEPENDABILITY_OPTIONS.filter((o) =>
              filters.dependability_tier?.includes(o.value),
            )}
            onChange={(_, newValue) =>
              updateFilter({
                dependability_tier: newValue.map((v) => v.value),
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Dependability" />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* Suspension Status - multi */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={SUSPENSION_STATUS_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={SUSPENSION_STATUS_OPTIONS.filter((o) =>
              filters.accountability_status?.includes(o.value),
            )}
            onChange={(_, newValue) =>
              updateFilter({
                accountability_status: newValue.map((v) => v.value),
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Suspension Status" />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
          />

          {/* Skills - multi autocomplete */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={skills}
            getOptionLabel={(opt) => opt.name}
            value={selectedSkillObjects}
            onChange={(_, newValue) =>
              updateFilter({ skill_ids: newValue.map((v) => v.id) })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Skills" />}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />

          {/* Zip + Radius */}
          <Stack gap={8}>
            <TextField
              label="Zip Code"
              placeholder="e.g. 10001"
              size="small"
              fullWidth
              value={filters.zip || ''}
              onChange={(e) => updateFilter({ zip: e.target.value })}
            />
            <TextField
              label="Radius (mi)"
              placeholder="e.g. 50"
              size="small"
              type="number"
              fullWidth
              value={filters.radius || ''}
              onChange={(e) => updateFilter({ radius: e.target.value })}
            />
          </Stack>
        </FilterGrid>

        <Stack
          gap={8}
          style={{ justifyContent: 'flex-end', marginTop: '12px' }}
        >
          <Button
            variant="text"
            size="small"
            onClick={onClear}
            sx={{ textTransform: 'none' }}
          >
            Clear All
          </Button>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default WorkerFilterPanel;
